import * as FBAuth from 'firebase/auth';
import { FunctionComponent, PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { isAdmin } from '../Authentication';
import { useForceRender } from '../components/hooks/ForceRender';
import { auth } from '../config/Firebase';

type AuthContextResult = {
	user: FBAuth.User | undefined;
	isAdmin: boolean;
	isSignedIn: boolean;
	loading: boolean;
};

const AuthContext = createContext<AuthContextResult | undefined>(undefined);

export const AuthProvider: FunctionComponent<PropsWithChildren> = (props) => {
	const [loading, setLoading] = useState(true);
	const [admin, setAdmin] = useState<boolean>(false);
	const { forceRender } = useForceRender();

	useEffect(function listenForChanges() {
		const unsubscribe = auth.onAuthStateChanged(async (user) => {
			setAdmin((user ? await isAdmin(user) : false));
			setLoading(false);
			forceRender();
		});

		return unsubscribe;
	}, []);

	const result = {
		user: auth.currentUser ?? undefined,
		isAdmin: admin,
		isSignedIn: auth.currentUser !== null,
		loading,
	};

	return <AuthContext.Provider value={result}>
		{props.children}
	</AuthContext.Provider>;
};

/** Can be used when auth state is unknown */
export function useUnknownAuthState() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error('useUnknownAuthState must be used within an AuthProvider');
	}
	return context;
}

/** Should only be called from within the authenticated stack. See Routes.tsx */
export function useAuthenticatedUser(): { user: FBAuth.User; } & Pick<AuthContextResult, 'isAdmin'> {
	const context = useContext(AuthContext);
	if (!context?.user) {
		throw new Error('useAuthenticatedUser should only be called within an authenticated route');
	}
	return {
		user: context.user,
		isAdmin: context.isAdmin,
	};
}
